import { formatAsnWithName } from 'pages/workspace/asset/custom-rules/components/network-values'
import { CriteriaModifierType } from 'api-models'
import { ChevronRight as RiArrowDropRightLine } from 'components/icons'
import { capitalizeFirstLetterFromArray } from 'utils/text'
import { getCurrentModifier } from '../../../../utils'
import { RuleFormType } from '../../../rule-form/types'
import { buildPrefix, isModifierPresent } from '../../utils'

export const AdditionalCriteria = (props: {
	modifiers: RuleFormType['modifiers']
	threshold: RuleFormType['threshold']
	titleClass?: string
}) => {
	const { modifiers, threshold , titleClass} = props

	return (
		<div className="col-span-2 text-sm">
			<span className={`text-t-gray-checkout font-medium ${titleClass}`}>AND</span>
			<div className={`mt-1 text-t-secondary ${!titleClass && 'pl-2 '}`}>
				<strong className="font-bold mr-1">
					{threshold.type === 'all'
						? 'All'
						: `${threshold.percentage_value || 70}%`}
				</strong>
				of those clicks
				<div className="flex flex-col gap-0.5  w-full mt-0.5">
					<NoDeviceModifier modifiers={modifiers} />
					<BadClickModifier modifiers={modifiers} />
					<VPNModifier modifiers={modifiers} />
					<ThreatModifier modifiers={modifiers} />
					<IpUsageModifier modifiers={modifiers} />
					<DeviceTypeModifier modifiers={modifiers} />
					<CountryModifier modifiers={modifiers} />
					<NetworkModifier modifiers={modifiers} kind="isp" />
					<NetworkModifier modifiers={modifiers} kind="asn" />
					<SearchKeywordModifier modifiers={modifiers} />
					<WebsiteInteractionModifier modifiers={modifiers} />
					<BotModifier modifiers={modifiers} />
					<TimeSpentModifier modifiers={modifiers} />
					<PageViewsModifier modifiers={modifiers} />
					<ConversionModifier modifiers={modifiers} />
				</div>
			</div>
		</div>
	)
}

const NoDeviceModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.NO_DEVICE

	if (isModifierPresent(modifiers, type))
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" /> has
				<strong className="text-t-bold ml-0.5">bounced</strong>
			</div>
		)

	return null
}

const BadClickModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.BAD_CLICKS

	if (isModifierPresent(modifiers, type))
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" /> was
				previously
				<strong className="text-t-bold ml-0.5">detected as bad</strong>
			</div>
		)

	return null
}

const VPNModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.PROXY

	if (isModifierPresent(modifiers, type)) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				are coming<strong className="text-t-bold ml-0.5">from VPN</strong>
			</div>
		)
	}

	return null
}

const ThreatModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.THREAT_LEVEL
	const modifier = getCurrentModifier(modifiers, type)

	if (
		modifier &&
		modifier.threat_level &&
		modifier.threat_level.values.length > 0
	) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				threat
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.threat_level?.negative)}{' '}
					{capitalizeFirstLetterFromArray(modifier?.threat_level?.values).join(
						', '
					)}
				</strong>
			</div>
		)
	}

	return null
}

const IpUsageModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.IP_USAGE
	const modifier = getCurrentModifier(modifiers, type)

	if (modifier && modifier.ip_usage && modifier.ip_usage.values.length > 0) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-3 w-3 text-t-default" />
				IP address type
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.ip_usage?.negative)}{' '}
					{modifier?.ip_usage?.values.join(', ')}
				</strong>
			</div>
		)
	}

	return null
}

const DeviceTypeModifier = (props: {
	modifiers: RuleFormType['modifiers']
}) => {
	const { modifiers } = props
	const type = CriteriaModifierType.DEVICE_TYPE
	const modifier = getCurrentModifier(modifiers, type)

	if (
		modifier &&
		modifier.device_type &&
		modifier.device_type.values.length > 0
	) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Device type
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.device_type?.negative)}{' '}
					{capitalizeFirstLetterFromArray(modifier?.device_type?.values).join(
						', '
					)}
				</strong>
			</div>
		)
	}

	return null
}

const CountryModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.COUNTRY
	const modifier = getCurrentModifier(modifiers, type)

	if (
		modifier &&
		modifier.country &&
		modifier.country.values &&
		modifier.country.values.length > 0
	) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Country
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier.country?.negative)}
					{modifier?.country?.values?.join(', ')}
				</strong>
			</div>
		)
	}

	return null
}

const SearchKeywordModifier = (props: {
	modifiers: RuleFormType['modifiers']
}) => {
	const { modifiers } = props
	const type = CriteriaModifierType.SEARCH_KEYWORD
	const modifier = getCurrentModifier(modifiers, type)

	const values =
		modifier?.keyword_text?.values ??
		(modifier?.keyword_text?.value ? [modifier.keyword_text.value] : [])
	if (modifier && values.length) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Search keyword
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier.keyword_text?.negative)}{' '}
					{values.join(', ')}
				</strong>
			</div>
		)
	}

	return null
}

const WebsiteInteractionModifier = (props: {
	modifiers: RuleFormType['modifiers']
}) => {
	const { modifiers } = props
	const type = CriteriaModifierType.INTERACTION
	const modifier = getCurrentModifier(modifiers, type)

	if (
		modifier &&
		modifier.user_interaction &&
		modifier.user_interaction.values.length > 0
	) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Website interaction
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.user_interaction?.negative)}
					{capitalizeFirstLetterFromArray(
						modifier?.user_interaction?.values
					).join(', ')}
				</strong>
			</div>
		)
	}

	return null
}

const BotModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.BOT_PROBABILITY
	const modifier = getCurrentModifier(modifiers, type)

	if (
		modifier &&
		modifier.bot_probability &&
		modifier.bot_probability.values.length > 0
	) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Bot probablity
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.bot_probability?.negative)}
					{capitalizeFirstLetterFromArray(
						modifier?.bot_probability?.values
					).join(', ')}
				</strong>
			</div>
		)
	}

	return null
}

const TimeSpentModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.TIME
	const modifier = getCurrentModifier(modifiers, type)

	if (modifier && modifier.time?.primary.value) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Have
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.time?.negative)}
					{modifier?.time?.primary.value} seconds time on site
				</strong>
			</div>
		)
	}

	return null
}

const PageViewsModifier = (props: { modifiers: RuleFormType['modifiers'] }) => {
	const { modifiers } = props
	const type = CriteriaModifierType.PAGE_VIEWS
	const modifier = getCurrentModifier(modifiers, type)

	if (modifier && modifier.page_views?.primary) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Have
				<strong className="text-t-bold ml-0.5">
					{buildPrefix(type, modifier?.page_views?.negative)}
					{modifier?.page_views?.primary} page views
				</strong>
			</div>
		)
	}

	return null
}

const ConversionModifier = (props: {
	modifiers: RuleFormType['modifiers']
}) => {
	const { modifiers } = props
	const conversionModifier = getCurrentModifier(
		modifiers,
		CriteriaModifierType.CONVERSION
	)

	if (conversionModifier) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Conversion
				<strong className="text-t-bold ml-0.5">was recorded</strong>
				{conversionModifier.conversion?.mode === 'with label' ? (
					<span className="ml-0.5">
						with label {conversionModifier.conversion.label}
					</span>
				) : null}
			</div>
		)
	}

	const notConversionModifier = getCurrentModifier(
		modifiers,
		CriteriaModifierType.NOT_CONVERSION
	)

	if (notConversionModifier) {
		return (
			<div className="flex items-center">
				<RiArrowDropRightLine className="h-4 w-4 text-t-google-ad" />
				Conversion
				<strong className="text-t-bold ml-0.5">was not recorded</strong>
			</div>
		)
	}

	return null
}

const NetworkModifier = ({
	modifiers,
	kind
}: {
	modifiers: RuleFormType['modifiers']
	kind: 'isp' | 'asn'
}) => {
	const modifier = getCurrentModifier(
		modifiers,
		kind === 'isp' ? CriteriaModifierType.ISP : CriteriaModifierType.ASN
	)
	const values =
		kind === 'isp'
			? modifier?.isp?.values
			: modifier?.asn?.values.map((value) =>
					formatAsnWithName(value, modifier.asn?.names?.[String(value)])
			  )
	if (!values?.length) return null
	return (
		<div className="flex items-start gap-1">
			<RiArrowDropRightLine className="h-4 w-4 shrink-0 text-t-google-ad" />
			<span className="min-w-0 break-words">
				{kind.toUpperCase()} {modifier?.[kind]?.negative ? 'is not' : 'is'}{' '}
				<strong>{values.join(', ')}</strong>
			</span>
		</div>
	)
}
